import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import UserAvatar from './UserAvatar';
import CreateCompanyModal from './CreateCompanyModal';
import axios from '../config/axios';
import { canAccessFeature } from '../utils/planAccess';
import { toggleTheme as toggleAppTheme } from '../utils/theme';
import './Navbar.css';

function buildNavItems(user) {
  const hasCompany = user?.active_company_id || user?.company_id;
  const isSuperAdmin = user?.role === 'super_admin';

  if (!hasCompany) {
    if (isSuperAdmin) {
      return [
        {
          id: 'companies',
          type: 'link',
          to: '/companies',
          icon: 'apartment',
          labelKey: 'companies',
          end: true,
          group: 'admin'
        },
        {
          id: 'users',
          type: 'link',
          to: '/users',
          icon: 'group',
          labelKey: 'users',
          end: true,
          group: 'admin'
        }
      ];
    }
    return [];
  }

  const isAdmin =
    user.role === 'super_admin' ||
    (user.active_company_role || user.role) === 'admin';

  const items = [
    {
      id: 'dashboard',
      type: 'link',
      to: '/dashboard',
      icon: 'dashboard',
      labelKey: 'dashboard',
      end: true,
      group: 'primary'
    },
    {
      id: 'tasks',
      type: 'link',
      to: '/tasks',
      icon: 'assignment',
      labelKey: 'tasks',
      badge: true,
      group: 'primary'
    }
  ];

  if (!isSuperAdmin) {
    items.push({
      id: 'time-report',
      type: 'link',
      to: '/time-report',
      icon: 'schedule',
      labelKey: 'timeReport',
      group: 'primary'
    });
  }

  if (isAdmin) {
    items.push({
      id: 'locations',
      type: 'link',
      to: '/locations',
      icon: 'location_on',
      labelKey: 'locations',
      group: 'admin'
    });
    items.push({
      id: 'customers',
      type: 'link',
      to: '/customers',
      icon: 'contacts',
      labelKey: 'customers',
      group: 'admin'
    });
    items.push({
      id: 'articles',
      type: 'link',
      to: '/articles',
      icon: 'category',
      labelKey: 'articles',
      group: 'admin'
    });
  }

  if (canAccessFeature(user, 'handouts')) {
    items.push({
      id: 'handouts',
      type: 'link',
      to: '/handouts',
      icon: 'inventory_2',
      labelKey: 'handouts',
      group: isAdmin ? 'admin' : 'primary'
    });
  }

  if (isAdmin && canAccessFeature(user, 'taskWorkGroups')) {
    items.push({
      id: 'work-groups',
      type: 'link',
      to: '/work-groups',
      icon: 'group_work',
      labelKey: 'workGroups',
      group: 'admin'
    });
  }

  if (isAdmin) {
    if (canAccessFeature(user, 'taskPresets') || canAccessFeature(user, 'checklistPresets')) {
      items.push({ id: 'presets', type: 'presets', group: 'admin' });
    }
    items.push({
      id: 'task-export',
      type: 'link',
      to: '/task-export',
      icon: 'download',
      labelKey: 'taskExport',
      group: 'admin'
    });
    if (!isSuperAdmin) {
      items.push({
        id: 'users',
        type: 'link',
        to: '/users',
        icon: 'group',
        labelKey: 'users',
        end: true,
        group: 'admin'
      });
    }
  }

  if (isSuperAdmin) {
    items.push({
      id: 'companies',
      type: 'link',
      to: '/companies',
      icon: 'apartment',
      labelKey: 'companies',
      end: true,
      group: 'admin'
    });
    items.push({
      id: 'users',
      type: 'link',
      to: '/users',
      icon: 'group',
      labelKey: 'users',
      end: true,
      group: 'admin'
    });
  }

  if (isAdmin && canAccessFeature(user, 'advancedAnalytics')) {
    items.push({
      id: 'analytics',
      type: 'link',
      to: '/analytics',
      icon: 'analytics',
      labelKey: 'analytics',
      group: 'insights'
    });
  }

  return items;
}

const Navbar = () => {
  const { t } = useTranslation(['nav', 'common']);
  const nav = (key) => t(key, { ns: 'nav' });
  const { user, logout, fetchUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isDark, setIsDark] = useState(document.documentElement.dataset.theme === 'dark');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPresetsSubmenu, setShowPresetsSubmenu] = useState(false);
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(max-width: 768px)').matches;
  });
  const [showInvitations, setShowInvitations] = useState(false);
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const [navError, setNavError] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [newTasksCount, setNewTasksCount] = useState(0);
  const [invitations, setInvitations] = useState([]);
  const dropdownRef = useRef(null);
  const drawerRef = useRef(null);
  const hamburgerRef = useRef(null);
  const mobileAvatarRef = useRef(null);

  const navItems = useMemo(() => (user ? buildNavItems(user) : []), [user]);

  const presetsActive =
    location.pathname.startsWith('/task-presets') ||
    location.pathname.startsWith('/preset-checklists');

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia('(max-width: 768px)');
    if (!mq) {
      return undefined;
    }
    const handleMobileChange = (event) => {
      setIsMobile(event.matches);
      if (!event.matches) {
        setShowMobileDrawer(false);
      }
    };
    setIsMobile(mq.matches);
    mq.addEventListener('change', handleMobileChange);
    return () => mq.removeEventListener('change', handleMobileChange);
  }, []);

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(document.documentElement.dataset.theme === 'dark');
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchCompanyName = async () => {
      const activeCompanyId = user?.active_company_id || user?.company_id;
      if (activeCompanyId) {
        try {
          const response = await axios.get(`/api/companies/${activeCompanyId}`);
          setCompanyName(response.data.data?.name || '');
        } catch (error) {
          console.error('Error fetching company name:', error);
        }
      } else {
        setCompanyName('');
      }
    };

    const fetchInvitations = async () => {
      if (user?.email) {
        try {
          const response = await axios.get('/api/companies/invitations');
          setInvitations(response.data.data || []);
        } catch (error) {
          if (error.response?.status !== 403) {
            console.error('Error fetching invitations:', error);
          }
          setInvitations([]);
        }
      }
    };

    const fetchNewTasksCount = async () => {
      if (user) {
        try {
          const lastVisit = localStorage.getItem('tasks_last_visit');
          if (lastVisit) {
            const response = await axios.get(
              `/api/tasks/new-assignments-count?since=${encodeURIComponent(lastVisit)}`
            );
            setNewTasksCount(response.data.data?.count || 0);
          } else {
            setNewTasksCount(0);
          }
        } catch (error) {
          console.error('Error fetching new tasks count:', error);
        }
      }
    };

    if (user) {
      fetchCompanyName();
      fetchNewTasksCount();
      fetchInvitations();

      const handleTasksPageVisited = () => {
        fetchNewTasksCount();
      };
      window.addEventListener('tasksPageVisited', handleTasksPageVisited);

      const interval = setInterval(fetchNewTasksCount, 30000);

      return () => {
        clearInterval(interval);
        window.removeEventListener('tasksPageVisited', handleTasksPageVisited);
      };
    }
  }, [user]);

  useEffect(() => {
    setShowMobileDrawer(false);
    setShowDropdown(false);
  }, [location.pathname]);

  useEffect(() => {
    if (presetsActive) {
      setShowPresetsSubmenu(true);
    }
  }, [presetsActive]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inDrawer = drawerRef.current?.contains(event.target);
      const inHamburger = hamburgerRef.current?.contains(event.target);
      const inMobileAvatar = mobileAvatarRef.current?.contains(event.target);

      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !inMobileAvatar) {
        setShowDropdown(false);
      }
      if (showMobileDrawer && !inDrawer && !inHamburger && !inMobileAvatar) {
        setShowMobileDrawer(false);
        setShowDropdown(false);
        setShowInvitations(false);
      }
    };

    if (showDropdown || showMobileDrawer) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown, showMobileDrawer]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== 'Escape') {
        return;
      }
      if (showCreateCompanyModal) {
        return;
      }
      if (!showPresetsSubmenu && !showMobileDrawer && !showDropdown) {
        return;
      }
      if (showPresetsSubmenu) {
        setShowPresetsSubmenu(false);
        return;
      }
      if (showDropdown) {
        setShowDropdown(false);
        setShowInvitations(false);
        return;
      }
      if (showMobileDrawer) {
        setShowMobileDrawer(false);
        hamburgerRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showCreateCompanyModal, showPresetsSubmenu, showMobileDrawer, showDropdown]);

  useEffect(() => {
    if (!isMobile) {
      document.body.classList.remove('navbar-drawer-open');
      return undefined;
    }
    document.body.classList.toggle('navbar-drawer-open', showMobileDrawer);
    return () => document.body.classList.remove('navbar-drawer-open');
  }, [isMobile, showMobileDrawer]);

  useEffect(() => {
    const aside = drawerRef.current;
    if (!aside) {
      return undefined;
    }
    if (isMobile && !showMobileDrawer) {
      aside.setAttribute('inert', '');
    } else {
      aside.removeAttribute('inert');
    }
    return undefined;
  }, [isMobile, showMobileDrawer]);

  const toggleTheme = () => {
    const next = toggleAppTheme();
    setIsDark(next === 'dark');
  };

  const handleLogout = () => {
    setShowDropdown(false);
    logout();
    navigate('/login');
  };

  const handleUserSettings = () => {
    setShowDropdown(false);
    navigate('/users/edit');
  };

  const handleRequestSupport = () => {
    setShowDropdown(false);
    navigate('/support');
  };

  const handleCompanySettings = () => {
    setShowDropdown(false);
    navigate('/my-company');
  };

  const handleManageUsers = () => {
    setShowDropdown(false);
    navigate('/users');
  };

  const handleCreateCompany = () => {
    setShowDropdown(false);
    setShowCreateCompanyModal(true);
  };

  const handleCreateCompanySuccess = async () => {
    if (fetchUser) {
      await fetchUser();
    }
    setShowCreateCompanyModal(false);
    navigate('/dashboard');
  };

  const handleSwitchCompany = async (companyId) => {
    try {
      setNavError('');
      const response = await axios.post(`/api/companies/${companyId}/switch`);
      if (response.data.success) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error switching company:', error);
      setNavError(nav('failedSwitchCompany'));
    }
  };

  const handleAcceptInvitation = async (invitation) => {
    try {
      setNavError('');
      const companyId = invitation.company_id || invitation.id;
      const response = await axios.post(`/api/companies/${companyId}/join`);
      if (response.data.success) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setNavError(error.response?.data?.error || t('failedAcceptInvitation', { ns: 'common' }));
    }
  };

  const handleDeclineInvitation = async (invitation) => {
    try {
      setNavError('');
      const companyId = invitation.company_id || invitation.id;
      const response = await axios.post(`/api/companies/${companyId}/decline`);
      if (response.data.success) {
        setInvitations((prev) =>
          prev.filter((inv) => (inv.company_id || inv.id) !== companyId)
        );
        if (fetchUser) fetchUser();
      }
    } catch (error) {
      console.error('Error declining invitation:', error);
      setNavError(error.response?.data?.error || t('failedDeclineInvitation', { ns: 'common' }));
    }
  };

  const closeDrawer = () => {
    setShowMobileDrawer(false);
    setShowDropdown(false);
    setShowInvitations(false);
  };

  const linkClassName = ({ isActive }) =>
    `navbar-link${isActive ? ' navbar-link-active' : ''}`;

  const renderNavLink = (item) => {
    if (item.id === 'tasks') {
      return (
        <NavLink
          key={item.id}
          to="/tasks"
          className={({ isActive }) =>
            `navbar-link navbar-link-with-badge${isActive ? ' navbar-link-active' : ''}`
          }
          onClick={closeDrawer}
        >
          <span className="material-symbols-outlined">assignment</span>
          <span>{nav('tasks')}</span>
          {newTasksCount > 0 && (
            <span className="navbar-notification-badge">{newTasksCount}</span>
          )}
        </NavLink>
      );
    }

    return (
      <NavLink
        key={item.id}
        to={item.to}
        end={Boolean(item.end)}
        className={linkClassName}
        onClick={closeDrawer}
      >
        {item.icon && (
          <span className="material-symbols-outlined">{item.icon}</span>
        )}
        <span>{item.labelKey ? nav(item.labelKey) : ''}</span>
      </NavLink>
    );
  };

  const renderPresets = () => (
    <div className="navbar-presets-group">
      <button
        type="button"
        className={`navbar-link navbar-presets-btn${
          showPresetsSubmenu || presetsActive ? ' active' : ''
        }${presetsActive ? ' navbar-link-active' : ''}`}
        onClick={() => setShowPresetsSubmenu((open) => !open)}
        aria-expanded={showPresetsSubmenu}
        aria-controls="navbar-presets-panel"
      >
        <span className="material-symbols-outlined">tune</span>
        <span>{nav('presets')}</span>
        <span className="material-symbols-outlined navbar-submenu-chevron">
          {showPresetsSubmenu ? 'expand_less' : 'expand_more'}
        </span>
      </button>
      {showPresetsSubmenu && (
        <div id="navbar-presets-panel" className="navbar-presets-submenu">
          <NavLink
            to="/task-presets"
            className={({ isActive }) =>
              `navbar-presets-item${isActive ? ' navbar-link-active' : ''}`
            }
            onClick={closeDrawer}
          >
            <span>{nav('presetTask')}</span>
          </NavLink>
          <NavLink
            to="/preset-checklists"
            className={({ isActive }) =>
              `navbar-presets-item${isActive ? ' navbar-link-active' : ''}`
            }
            onClick={closeDrawer}
          >
            <span>{nav('presetChecklist')}</span>
          </NavLink>
        </div>
      )}
    </div>
  );

  const renderNavItems = () => {
    const nodes = [];
    let previousGroup = null;

    navItems.forEach((item) => {
      if (previousGroup && item.group && item.group !== previousGroup) {
        nodes.push(
          <div
            key={`nav-divider-${previousGroup}-${item.group}`}
            className="navbar-nav-divider"
            role="separator"
          />
        );
      }
      previousGroup = item.group || previousGroup;

      if (item.type === 'presets') {
        nodes.push(<React.Fragment key={item.id}>{renderPresets()}</React.Fragment>);
      } else {
        nodes.push(renderNavLink(item));
      }
    });

    return nodes;
  };
  const renderUserMenu = () => (
    <div className="navbar-user-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className="navbar-user-avatar-btn"
        onClick={() => setShowDropdown(!showDropdown)}
        aria-label={nav('userMenu')}
      >
        <UserAvatar user={user} size={32} />
        <span className="navbar-user-avatar-label">
          {user.first_name} {user.last_name}
        </span>
        <span className="material-symbols-outlined navbar-user-chevron">
          {showDropdown ? 'expand_less' : 'expand_more'}
        </span>
      </button>
      {showDropdown && (
        <div className="navbar-dropdown">
          <div className="navbar-dropdown-header">
            <UserAvatar user={user} size={40} />
            <div className="navbar-dropdown-user-info">
              <div className="navbar-dropdown-username">
                {user.first_name} {user.last_name}
              </div>
              <div className="navbar-dropdown-company">
                {companyName || nav('noCompany')}
              </div>
            </div>
          </div>
          <div className="navbar-dropdown-divider" />

          {user?.companies && user.companies.length > 1 && (
            <>
              <div className="navbar-dropdown-section-title">{nav('companiesSection')}</div>
              {user.companies.map((company) => {
                const isActive =
                  (user.active_company_id || user.company_id) === company.id;
                return (
                  <button
                    key={company.id}
                    type="button"
                    className={`navbar-dropdown-item ${isActive ? 'active' : ''}`}
                    onClick={() => {
                      if (!isActive) {
                        handleSwitchCompany(company.id);
                      }
                      setShowDropdown(false);
                    }}
                  >
                    <span className="material-symbols-outlined">
                      {isActive ? 'check_circle' : 'circle'}
                    </span>
                    <span>{company.name}</span>
                    <span className="navbar-dropdown-role-badge">
                      {company.role}
                    </span>
                  </button>
                );
              })}
              <div className="navbar-dropdown-divider" />
            </>
          )}

          {(user?.invitationsCount > 0 || invitations.length > 0) && (
            <>
              <button
                type="button"
                className="navbar-dropdown-item"
                onClick={() => setShowInvitations(!showInvitations)}
              >
                <span className="material-symbols-outlined">mail</span>
                <span>{nav('invitations')}</span>
                <span className="navbar-notification-badge">
                  {user?.invitationsCount || invitations.length}
                </span>
              </button>
              {showInvitations && invitations.length > 0 && (
                <div className="navbar-dropdown-invitations">
                  {invitations.map((invitation) => {
                    const invitationId =
                      invitation.invitation_id || invitation.id;
                    return (
                      <div
                        key={invitationId}
                        className="navbar-dropdown-invitation-item"
                      >
                        <div className="navbar-dropdown-invitation-info">
                          <div className="navbar-dropdown-invitation-company">
                            {invitation.name}
                          </div>
                          {invitation.invited_by_name && (
                            <div className="navbar-dropdown-invitation-by">
                              {t('invitedBy', { ns: 'nav', name: invitation.invited_by_name })}
                            </div>
                          )}
                        </div>
                        <div className="navbar-dropdown-invitation-actions">
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => handleAcceptInvitation(invitation)}
                          >
                            {nav('accept')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleDeclineInvitation(invitation)}
                          >
                            {nav('decline')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="navbar-dropdown-divider" />
            </>
          )}

          <div className="navbar-dropdown-section-title">{nav('account')}</div>
          <button
            type="button"
            className="navbar-dropdown-item"
            onClick={handleUserSettings}
          >
            <span className="material-symbols-outlined">person</span>
            <span>{nav('settings')}</span>
          </button>
          <button
            type="button"
            className="navbar-dropdown-item"
            onClick={handleRequestSupport}
          >
            <span className="material-symbols-outlined">support_agent</span>
            <span>{nav('requestSupport')}</span>
          </button>
          <button
            type="button"
            className="navbar-dropdown-item"
            onClick={handleCreateCompany}
          >
            <span className="material-symbols-outlined">add_business</span>
            <span>{nav('createCompany')}</span>
          </button>
          {(user.role === 'super_admin' || user.active_company_role === 'admin') && (
            <>
              <div className="navbar-dropdown-section-title">{nav('company')}</div>
              <button
                type="button"
                className="navbar-dropdown-item"
                onClick={handleCompanySettings}
              >
                <span className="material-symbols-outlined">groups</span>
                <span>{nav('companySettings')}</span>
              </button>
              <button
                type="button"
                className="navbar-dropdown-item"
                onClick={handleManageUsers}
              >
                <span className="material-symbols-outlined">group</span>
                <span>{nav('manageUsers')}</span>
              </button>
            </>
          )}
          <div className="navbar-dropdown-divider" />
          <button
            type="button"
            className="navbar-dropdown-item"
            onClick={toggleTheme}
            aria-label={isDark ? nav('switchToLightMode') : nav('switchToDarkMode')}
          >
            <span>{isDark ? nav('lightMode') : nav('darkMode')}</span>
            <div className={`navbar-toggle-switch ${isDark ? 'active' : ''}`}>
              <div className="navbar-toggle-knob" />
            </div>
          </button>
          <div className="navbar-dropdown-divider" />
          <button
            type="button"
            className="navbar-dropdown-item"
            onClick={handleLogout}
          >
            <span className="material-symbols-outlined">logout</span>
            <span>{nav('logOut')}</span>
          </button>
        </div>
      )}
    </div>
  );

  if (!user) {
    return null;
  }

  const logoPath = isDark
    ? '/service_in_mind_logo_white.png'
    : '/service_in_mind_logo_dark.png';

  const brandTo =
    user.active_company_id || user.company_id ? '/dashboard' : '/no-company';

  return (
    <>
      {navError && <div className="navbar-error error">{navError}</div>}

      <header className="navbar-mobile-bar">
        <button
          type="button"
          ref={hamburgerRef}
          className={`navbar-hamburger-btn${showMobileDrawer ? ' active' : ''}`}
          onClick={() => setShowMobileDrawer((open) => !open)}
          aria-expanded={showMobileDrawer}
          aria-controls="app-sidebar"
          aria-label={nav('navigationMenu')}
        >
          <span className="material-symbols-outlined">
            {showMobileDrawer ? 'close' : 'menu'}
          </span>
        </button>
        <Link to={brandTo} className="navbar-brand navbar-brand-mobile">
          <img src={logoPath} alt="Service in Mind" className="navbar-logo" />
        </Link>
        <button
          type="button"
          ref={mobileAvatarRef}
          className="navbar-mobile-avatar-btn"
          onClick={(event) => {
            event.stopPropagation();
            setShowMobileDrawer(true);
            setShowDropdown(true);
          }}
          aria-label={nav('account')}
          aria-hidden={showMobileDrawer ? true : undefined}
          tabIndex={showMobileDrawer ? -1 : undefined}
        >
          <UserAvatar user={user} size={32} />
        </button>
      </header>

      {showMobileDrawer && (
        <button
          type="button"
          className="navbar-drawer-backdrop"
          aria-label={t('close', { ns: 'common', defaultValue: 'Close navigation' })}
          onClick={closeDrawer}
        />
      )}

      <aside
        id="app-sidebar"
        ref={drawerRef}
        className={`navbar navbar-sidebar${showMobileDrawer ? ' navbar-sidebar-open' : ''}`}
        aria-hidden={isMobile && !showMobileDrawer ? true : undefined}
      >
        <div className="navbar-sidebar-top">
          <Link to={brandTo} className="navbar-brand" onClick={closeDrawer}>
            <img src={logoPath} alt="Service in Mind" className="navbar-logo" />
          </Link>
          <nav className="navbar-nav" aria-label={nav('navigationMenu')}>
            {renderNavItems()}
          </nav>
        </div>
        <div className="navbar-sidebar-bottom">{renderUserMenu()}</div>
      </aside>

      <CreateCompanyModal
        isOpen={showCreateCompanyModal}
        onClose={() => setShowCreateCompanyModal(false)}
        onSuccess={handleCreateCompanySuccess}
      />
    </>
  );
};

export default Navbar;
export { buildNavItems };
